import { useContext, useCallback } from 'react';
import _get from 'lodash/get';
import _find from 'lodash/find';
import { AjaxContext, formatListData, AppContext, UserContext } from 'commons-ui';
import { UserDetailsContext } from 'context/userDetails';
import { ACTION, PAGE_NAMES, USER_PROFILE_RESOURCES } from 'constant';
import { TransactionHistoryFilters } from './constant';

export interface TransactionPdfParams {
	minTime: number;
	maxTime: number;
	tidsToHighlight: string[];
}

const useService = () => {
	const { postData } = useContext(AjaxContext);
	const {
		app: { appName }
	} = useContext(AppContext);
	const {
		selectedUser: { uid }
	} = useContext(UserDetailsContext);
	const { permissionsConfig } = useContext(UserContext);
	const getTransactionPdf = useCallback(async ({ minTime, maxTime, tidsToHighlight = [] }: TransactionPdfParams) => {
		const result = await postData({
			endpoint: `/dashboard/getTransactionPdf/${PAGE_NAMES.userProfile}-${USER_PROFILE_RESOURCES.transactionHistory}-getTransactionPdf`,
			queryParams: {
				appName
			},
			body: {
				uid,
				tidsToHighlight,
				minTime,
				maxTime
			}
		});
		return result?.data?.fileUrl;
	}, []);

	const getCurrentBalance = useCallback(async () => {
		const permission = _find(permissionsConfig, { actionName: ACTION.getWalletDetails });
		const result = await postData({
			endpoint: `/dashboard/getWalletDetails/${PAGE_NAMES.userProfile}-${USER_PROFILE_RESOURCES.transactionHistory}-getWalletDetails`,
			queryParams: {
				appName
			},
			body: { uid },
			customHeaders: {
				isMasked: permission?.isMasked || false
			}
		});
		const data = _get(result, ['data', 'data'], {});
		const { addedCoins = 0, unlockedCoins = 0, actualCoins = 0, bonusCoins = 0, cashbackCoins = 0 } = data;
		return ((addedCoins + unlockedCoins + actualCoins + bonusCoins + cashbackCoins) / 100).toFixed(2);
	}, []);

	const getTransactionHistory = useCallback(
		async (
			filters?: { dateRange: TransactionHistoryFilters; transactionTypes: number[] },
			pageDetails?: { pageNo: number; pageSize: number }
		) => {
			const pageNo: number = pageDetails?.pageNo || 1;
			const pageSize: number = pageDetails?.pageSize || 20;
			const skipCount: number = (pageNo - 1) * pageSize;
			const permission = _find(permissionsConfig, { actionName: ACTION.getTransactionDetails });

			const result = await postData({
				endpoint: `/dashboard/getTransactionDetails/${PAGE_NAMES.userProfile}-${USER_PROFILE_RESOURCES.transactionHistory}-getTransactionDetails`,
				queryParams: {
					appName
				},
				body: { uid, ...(filters?.dateRange || {}), skipCount, transactionTypes: [] },
				customHeaders: {
					isMasked: permission?.isMasked || false
				}
			});

			const listData = formatListData(_get(result, ['data', 'data'], []));
			return { rowCount: listData[0].totalCount, data: listData };
		},
		[]
	);

	return {
		getTransactionHistory,
		getCurrentBalance,
		getTransactionPdf
	};
};

export default useService;
